import { useState, useCallback, useRef } from 'react';
import axios from '../services/api';

/**
 * Custom hook for managing upload workflow state
 *
 * Stages: idle → uploading → processing → curated → complete → error
 *
 * Features:
 * - Clean stage transitions
 * - Progress tracking
 * - Error handling with retry
 * - Cancellation support
 */
export function useUploadWorkflow() {
  const [stage, setStage] = useState('idle');
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [data, setData] = useState(null);
  const abortControllerRef = useRef(null);

  /**
   * Upload file to server
   * @param {File} file - File to upload
   * @param {string} endpoint - API endpoint ('/analyze/upload' or '/analyze/ocr')
   * @param {Object} params - Query parameters (category, club_type, dexterity, etc.)
   */
  const upload = useCallback(async (file, endpoint, params) => {
    // Reset state
    setError(null);
    setProgress(0);
    setStage('uploading');

    // Create abort controller for cancellation
    abortControllerRef.current = new AbortController();

    try {
      // Validate file
      const isOCR = endpoint.includes('ocr');
      const maxSize = isOCR ? 10 * 1024 * 1024 : 5 * 1024 * 1024;

      if (file.size > maxSize) {
        throw new Error(`File too large. Maximum size is ${maxSize / 1024 / 1024}MB.`);
      }

      if (isOCR && !file.type.startsWith('image/')) {
        throw new Error('Please upload an image file (JPG, PNG, etc.)');
      }

      if (!isOCR && !file.name.toLowerCase().endsWith('.csv')) {
        throw new Error('Please upload a CSV file');
      }

      // Create form data
      const formData = new FormData();
      formData.append('file', file);

      // Build query string
      const queryString = new URLSearchParams(params).toString();
      const url = `${endpoint}?${queryString}`;

      // Upload with progress tracking
      // OCR endpoints can take longer - use 95s timeout (cascade + matrix)
      const response = await axios.post(url, formData, {
        signal: abortControllerRef.current.signal,
        timeout: 95000, // 95s timeout for OCR cascade + matrix calc
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setProgress(percentCompleted);
        }
      });

      if (response.data.status === 'error') {
        throw new Error(response.data.message);
      }

      // Store data and move to next stage
      setData(response.data);
      setProgress(100);

      // If multi-shot upload, move to curation stage
      if (response.data.shots && response.data.shots.length > 1) {
        setStage('curated');
      } else {
        setStage('complete');
      }

      return response.data;
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        setError('Upload canceled');
        setStage('idle');
      } else if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
        const errorMsg = 'Upload timed out. Please try a smaller file or check your connection.';
        setError(errorMsg);
        setStage('error');
      } else {
        const errorMsg = err.response?.data?.detail || err.message || 'Upload failed';
        setError(errorMsg);
        setStage('error');
      }
      throw err;
    }
  }, []);

  /**
   * Update data after shot curation
   */
  const setCuratedData = useCallback((curatedData) => {
    setData(prev => ({ ...prev, curated: curatedData }));
    setStage('complete');
  }, []);

  /**
   * Cancel ongoing upload
   */
  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  /**
   * Reset workflow to initial state
   */
  const reset = useCallback(() => {
    setStage('idle');
    setError(null);
    setProgress(0);
    setData(null);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  /**
   * Retry after error
   */
  const retry = useCallback((file, endpoint, params) => {
    return upload(file, endpoint, params);
  }, [upload]);

  return {
    // State
    stage,
    error,
    progress,
    data,

    // Actions
    upload,
    setCuratedData,
    cancel,
    reset,
    retry,

    // Derived state
    isUploading: stage === 'uploading',
    isProcessing: stage === 'processing',
    isCurating: stage === 'curated',
    isComplete: stage === 'complete',
    hasError: stage === 'error',
    isIdle: stage === 'idle',
  };
}
